import React, { useEffect, useRef } from "react";

interface InjectedProps {
  // No additional props needed - this is a pure wrapper HOC
}

/**
 * Higher-Order Component that wraps a component to initialize font detection.
 * 
 * This HOC ensures that font detection runs after the wrapped component is mounted,
 * resolving timing issues with HTML attribute injection that can occur when using
 * the custom hook directly.
 * 
 * Usage:
 * ```tsx
 * import withFontDetection from '@/components/__hoc/withFontDetection';
 * 
 * const MyComponent = () => <div>Content</div>;
 * export default withFontDetection(MyComponent);
 * ```
 * 
 * @param WrappedComponent - The component to wrap
 * @param componentName - Optional custom name for logging (defaults to component's name)
 * @returns A wrapped component with font detection initialized
 */
function withFontDetection<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName = WrappedComponent.name || "Component"
) {
  return function FontDetectionWrapper(props: P) {
    const observerRef = useRef<MutationObserver | null>(null);
    const documentObserverRef = useRef<MutationObserver | null>(null);
    const isInitializedRef = useRef(false);

    useEffect(() => {
      if (
        typeof globalThis === "undefined" ||
        typeof getComputedStyle === "undefined"
      ) {
        return () => {};
      }

      // Prevent double initialization in React 18 Strict Mode
      if (isInitializedRef.current && observerRef.current) {
        return () => {};
      }

      isInitializedRef.current = true;

      // Inject data-font attribute on typography elements
      const injectFontAttributes = () => {
        const typographyElements = document.querySelectorAll(".typography, .copy-typography, .button-typography");

        typographyElements.forEach((el) => {
          // Get the computed font-family stack (this already accounts for CSS specificity and !important rules)
          const fontFamilyStack = globalThis.getComputedStyle(el).fontFamily;

          if (!fontFamilyStack) return;

          // Extract the first font from the stack (the active font being used by the browser)
          const activeFont = fontFamilyStack
            .split(",")[0]
            .trim()
            .replace(/['"]/g, "");

          el.setAttribute("data-font", activeFont);
        });
      };

      // Set up MutationObserver to watch for class changes on typography elements
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class"
          ) {
            const target = mutation.target as Element;
            if (target.classList.contains("typography")) {
              const fontFamilyStack =
                globalThis.getComputedStyle(target).fontFamily;
              if (fontFamilyStack) {
                const activeFont = fontFamilyStack
                  .split(",")[0]
                  .trim()
                  .replace(/['"]/g, "");
                target.setAttribute("data-font", activeFont);
              }
            }
          }
        });
      });

      observerRef.current = observer;

      // Initial injection with multiple fallback strategies
      const initializeFontDetection = () => {
        injectFontAttributes();

        // Observe all typography elements for class changes
        const observeTypographyElements = () => {
          const typographyElements = document.querySelectorAll(".typography, .copy-typography, .button-typography");
          typographyElements.forEach((el) => {
            observer.observe(el, {
              attributes: true,
              attributeFilter: ["class"],
            });
          });
        };

        observeTypographyElements();

        // Set up a document-level observer to catch dynamically added typography elements
        const documentObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (node instanceof Element) {
                if (node.classList.contains("typography")) {
                  const fontFamilyStack =
                    globalThis.getComputedStyle(node).fontFamily;
                  if (fontFamilyStack) {
                    const activeFont = fontFamilyStack
                      .split(",")[0]
                      .trim()
                      .replace(/['"]/g, "");
                    node.setAttribute("data-font", activeFont);
                    observer.observe(node, {
                      attributes: true,
                      attributeFilter: ["class"],
                    });
                  }
                }
                // Also observe children that might contain typography elements
                const nestedTypographyElements =
                  node.querySelectorAll(".typography, .copy-typography, .button-typography");
                nestedTypographyElements.forEach((el) => {
                  const fontFamilyStack =
                    globalThis.getComputedStyle(el).fontFamily;
                  if (fontFamilyStack) {
                    const activeFont = fontFamilyStack
                      .split(",")[0]
                      .trim()
                      .replace(/['"]/g, "");
                    el.setAttribute("data-font", activeFont);
                    observer.observe(el, {
                      attributes: true,
                      attributeFilter: ["class"],
                    });
                  }
                });
              }
            });
          });
        });

        documentObserverRef.current = documentObserver;
        documentObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      };

      // Use requestAnimationFrame for better timing
      // Triple requestAnimationFrame ensures the DOM is fully rendered and styles are applied
      globalThis.requestAnimationFrame(() => {
        globalThis.requestAnimationFrame(() => {
          globalThis.requestAnimationFrame(() => {
            initializeFontDetection();
          });
        });
      });

      // Cleanup observers on unmount
      return () => {
        observer.disconnect();
        const docObserver = documentObserverRef.current;
        if (docObserver) {
          docObserver.disconnect();
        }
      };
    }, []); // Empty dependency array - run only once on mount

    return <WrappedComponent {...props} />;
  };
}

export default withFontDetection;
